<template>
<div class="px-1 pt-1">
  <div class="input-group pb-3">
    <button class="btn btn-outline-secondary" @click="refresh()" :disabled="isLoading">
      <div v-if="isLoading" class="spinner-border spinner-border-sm"></div>
      <font-awesome-icon v-else :icon="['fas', 'rotate']" />
      <span class="d-none d-sm-inline">{{ $t('action.refresh') }}</span>
    </button>
    <button class="btn btn-outline-primary" @click="edit = !edit; refresh()">
      <font-awesome-icon :icon="['fas', edit ? 'shelves' : 'shelves-empty']" />
      {{ $t('action.edit') }}
    </button>
    <button class="btn btn-outline-primary" @click="showAll = !showAll; refresh()">
      <font-awesome-icon :icon="['fas', showAll ? 'eye-slash' : 'eye']" />
      {{ $t('action.show-all') }}
    </button>
    <span class="input-group-text ms-2">
      <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
    </span>
    <input id="itemFilter" type="search" class="form-control" v-model="itemFilter" :placeholder="$t('action.search')"/>
    <button v-if="itemFilter" type="button" class="btn btn-outline-secondary" @click="clearFilter" :title="$t('action.clear')">
      <font-awesome-icon :icon="['fas', 'filter-slash']" />
    </button>
  </div>

  <div v-if="error" class="alert alert-danger">
    {{ error }}
  </div>
  <div v-else-if="sortedDemands" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
    <table v-if="sortedDemands.length > 0" class="table table-hover table-sm table-fit align-middle">
      <thead>
        <tr>
          <th>
            {{ $t('field.generic.item') }}
            <font-awesome-icon :icon="['fas', 'sort-up']" />
          </th>
          <th>{{ $t('page.stockDemand.table.quantityDemand') }}</th>
          <th class="d-none d-lg-table-cell">{{ $t('page.stockDemand.table.stockQuantity') }}</th>
          <th class="d-none d-md-table-cell">Min/Max</th>
          <th class="d-none d-xl-table-cell">{{ $t('page.stockDemand.table.stockHistory') }}<br/><span class="text-secondary">({{ $t('global.days', 20) }})</span></th>
          <th class="d-none d-xl-table-cell">{{ $t('page.stockDemand.table.quantityOrdered') }}</th>
          <th class="d-none d-xl-table-cell">{{ $t('page.stockDemand.table.quantityDelivered') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="stockDemand in filteredItemsList" :key="(stockDemand.site.id, stockDemand.item.id)">
          <td class="d-md-none">
            <span class="text-primary fw-bold">{{ stockDemand.item.name }}&#8203;</span><sup class="text-secondary ms-1">{{ stockDemand.item.code }}</sup><br>
            <span v-if="stockDemand.item.name.localeCompare(stockDemand.item.description) != 0" class="fs-6 text-melodi">{{ stockDemand.item.description }}</span>
            <div class="input-group minmax-input">
              <input type="number" class="form-control" min="0" max="1000" v-model="stockDemand.minQuantity" :on-input="recalcDemand(stockDemand)" @focusout="updateItemMinMax(stockDemand)" :disabled="!edit">
              <span class="input-group-text text-secondary">/</span>
              <input type="number" class="form-control" min="0" max="1000" v-model="stockDemand.maxQuantity" :on-input="recalcDemand(stockDemand)" @focusout="updateItemMinMax(stockDemand)" :disabled="!edit">
              <span class="input-group-text text-secondary">{{ stockDemand.item.baseUnitCode }}</span>
            </div>
          </td>
          <td class="d-none d-md-table-cell"><ItemField :item="stockDemand.item" :show-copy-button="true" /></td>
          <td :class="demandClass(stockDemand)">
            <UnitQuantity :quantity="stockDemand.shownQuanity" :unit-name="stockDemand.item.baseUnitCode" class="fw-bold fs-5" />
            <RelativeDateTime :at="stockDemand.deliveryFulfilled ? undefined : stockDemand.minQuantityBreachedAt" :use-since="true" style="font-size: 0.75em;" />
          </td>
          <td class="d-none d-lg-table-cell"><QuantityField :quantity="stockDemand.stockQuantity" :unit-name="stockDemand.item.baseUnitCode" :pad-length="maxQuantityLength" /></td>
          <td class="d-none d-md-table-cell">
            <div class="input-group minmax-input">
              <input type="number" class="form-control" min="0" max="1000" v-model="stockDemand.minQuantity" :on-input="recalcDemand(stockDemand)" @focusout="updateItemMinMax(stockDemand)" :disabled="!edit">
              <span class="input-group-text text-secondary">/</span>
              <input type="number" class="form-control" min="0" max="1000" v-model="stockDemand.maxQuantity" :on-input="recalcDemand(stockDemand)" @focusout="updateItemMinMax(stockDemand)" :disabled="!edit">
              <span class="input-group-text text-secondary">{{ stockDemand.item.baseUnitCode }}</span>
            </div>
          </td>
          <td class="d-none d-xl-table-cell">
            <SparkLine v-if="historyData && historyData?.itemStockHistory"
                       :data="historyData?.itemStockHistory[stockDemand.item.id]!"
                       :bottom-line="stockDemand.minQuantity"
                       :high-line="stockDemand.maxQuantity"
            />
            <div v-else class="loading">
              <p class="placeholder-wave"><span class="placeholder col-12 placeholder-xs"></span></p>
            </div>
          </td>
          <td class="d-none d-xl-table-cell"><QuantityField :quantity="stockDemand.orderedQuantity" :unit-name="stockDemand.item.baseUnitCode" :pad-length="maxQuantityLength" /></td>
          <td class="d-none d-xl-table-cell"><QuantityField :quantity="stockDemand.deliveredQuantity" :unit-name="stockDemand.item.baseUnitCode" :pad-length="maxQuantityLength" /></td>
        </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
  <div v-else class="loading">
    {{ $t('status.loadingEllipsis') }}
  </div>
</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { storeToRefs } from 'pinia';
import { useStockDemandStore, useStockLevelsStore } from '@/stores';
import StockDemand from "@/models/frontend/StockDemand";
import { ItemField, QuantityField } from "@/components/fields";
import UnitQuantity from "@/components/UnitQuantity.vue";
import SparkLine from "@/components/diagram/SparkLine.vue";
import { useStockHistoryStore } from "@/stores/stockHistory.store.ts";
import RelativeDateTime from "@/components/RelativeDateTime.vue";

const stockDemandStore = useStockDemandStore()
const stockLevelStore = useStockLevelsStore();
const stockHistoryStore = useStockHistoryStore();
const { data: stockDemands, error } = storeToRefs(stockDemandStore);
const { data: historyData } = storeToRefs(stockHistoryStore);
const isLoading = ref(false);
const edit = ref(false);
const showAll = ref(false);
const itemFilter = ref('');
const offsetTop = ref(0);

onMounted(async () => {
  await refresh();
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const filteredItemsList = computed(() => {
  if (!itemFilter.value.trim()) {
    return sortedDemands.value;
  }
  
  const filterText = itemFilter.value.toLowerCase().trim();
  return sortedDemands.value.filter(entry => {
    const item = entry.item;
    return (
      item.name.toLowerCase().includes(filterText) ||
      item.code.toLowerCase().includes(filterText) ||
      (item.description && item.description.toLowerCase().includes(filterText))
    );
  });
});

const clearFilter = () => {
  itemFilter.value = '';
};

async function refresh() {
  isLoading.value = true;
  error.value = "";

  try {
    stockHistoryStore.fetchAll(20);
    await stockDemandStore.fetchAll(showAll.value);
  }
  finally {
    isLoading.value = false;
  }
}

const sortedDemands = computed(() => {
  return [...stockDemands.value]?.sort((a, b) => a.item.name.localeCompare(b.item.name));
});

const maxQuantityLength = computed(() => {
  return sortedDemands.value.reduce((acc, item) => Math.max(acc, item.quantity.toString().length), 0);
});

const updateItemMinMax = (demand: StockDemand) => {
  if (demand.minQuantity > demand.maxQuantity) {
    demand.maxQuantity = demand.minQuantity;
  }
  stockLevelStore.updateItemDemand(demand.stockLevelId, demand.minQuantity, demand.maxQuantity);
  demand.recalculate();
}

const recalcDemand = (demand: StockDemand) => {
  demand.recalculate();
}

function demandClass(stockDemand: StockDemand) {
  return {
    'bg-info text-dark': !stockDemand.hasDemand && stockDemand.canRefill,
    '': !stockDemand.hasDemand && !stockDemand.canRefill,
    'bg-success text-light': stockDemand.hasDemand && stockDemand.deliveryFulfilled,
    'bg-warning text-dark': stockDemand.hasDemand && !stockDemand.deliveryFulfilled && stockDemand.deliveryPartiallyFulfilled,
    'bg-danger text-light': stockDemand.hasDemand && !stockDemand.deliveryFulfilled && !stockDemand.deliveryPartiallyFulfilled
  };
}
</script>

<style scoped type="scss">
.minmax-input {
  width: 220px;
}

input[type=number] {
  text-align: right;
}
</style>